import React from 'react';
import { Link } from 'react-router-dom';
import Block, { ROW_LINK } from './Block';
import DetailsToggle from '../DetailsToggle';
import { useDetails } from '../../hooks/useDetails';
import { UpdatesData } from './types';
import { emojiOf } from './kudos';
import * as fmt from '../../lib/format';

export type UpdatesState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: UpdatesData };

/** How many lines show before "Show all". */
export const UPDATES_SHOWN = 5;

const LINK_LABEL: Record<string, string> = { waiting: 'See waiting tickets' };

// What changed in the person's projects over the last few days: a few plain lines, then the rest on request.
// It loads on its own, so a slow or failed read never holds back the rest of Home.
export default function UpdatesBlock({ state, onRetry, role, className = '', onGiveKudos }: { state: UpdatesState; onRetry: () => void; role: 'user' | 'leader' | 'admin'; className?: string; /** A leader's or admin's way to say thank you: the block's one action. */ onGiveKudos?: () => void }) {
  // Its own key (?updates=1): the page's Show details (?details=1) must not open the whole list as well.
  const [all, setAll] = useDetails('updates');

  // A leader or an admin who is on no project has nothing to say here, so the block is not shown at all.
  if (state.status === 'ready' && state.data.projects === 0 && state.data.lines.length === 0 && role !== 'user') return null;

  return (
    <Block title="Updates" className={className}>
      {onGiveKudos && (
        <div className="mb-2 flex justify-end">
          <button type="button" onClick={onGiveKudos} className={ROW_LINK}>Give kudos</button>
        </div>
      )}
      {state.status === 'loading' && (
        <p className="text-sm text-gray-600" role="status">
          Loading the updates…
        </p>
      )}

      {state.status === 'error' && (
        <p role="alert" className="text-sm text-rose-700">
          {state.message}{' '}
          <button type="button" onClick={onRetry} className="font-medium underline">
            Try again
          </button>
        </p>
      )}

      {state.status === 'ready' && state.data.projects === 0 && state.data.lines.length === 0 && (
        <p className="text-sm text-gray-600">Updates appear here once you are on a project. An admin can add you in Settings → Projects, or log time on a ticket and it appears.</p>
      )}

      {state.status === 'ready' && state.data.projects > 0 && state.data.lines.length === 0 && (
        <p className="text-sm text-gray-600">{`Nothing changed in your ${fmt.counted(state.data.projects, 'project')} in the last ${state.data.days === 1 ? 'day' : `${fmt.count(state.data.days)} days`}.`}</p>
      )}

      {state.status === 'ready' && state.data.lines.length > 0 && (
        <>
          <ul className="divide-y divide-gray-100">
            {(all ? state.data.lines : state.data.lines.slice(0, UPDATES_SHOWN)).map((l, i) => (
              <li key={`${i}:${l.project_key}:${l.kind}`} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2.5 first:pt-0 last:pb-0">
                <p className="min-w-0 text-sm text-gray-800">
                  {l.kind === 'kudos' && emojiOf(l.emoji) && (
                    <span role="img" aria-label={emojiOf(l.emoji)?.label} className="mr-1.5">
                      {emojiOf(l.emoji)?.glyph}
                    </span>
                  )}
                  {l.sentence}
                </p>
                {l.path && (
                  <Link to={l.path} className={ROW_LINK}>
                    {LINK_LABEL[l.kind] ?? 'Open'}
                  </Link>
                )}
              </li>
            ))}
          </ul>
          {state.data.lines.length > UPDATES_SHOWN && (
            <div className="mt-3">
              <DetailsToggle open={all} onChange={setAll} showLabel={`Show all ${fmt.count(state.data.lines.length)}`} hideLabel="Show fewer" />
            </div>
          )}
        </>
      )}
    </Block>
  );
}
